import { useCallback, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { setIsSiteUnavailable } from 'ducks/pageStatus/pageStatusSlice';

import { FEATURE_FLAGS, type FeatureFlag } from '@polyai/stream/feature-flags';

import api from 'api';
import type { FeaturesResponse } from 'api/internalResources/featureFlag';
import {
  GLOBAL_PERMISSION,
  useGlobalPermissions,
} from 'hooks/useProjectResources/useGlobalPermissions';
import { isFeatureEnabled } from 'lib/featuresHelper';
import {
  getLocalFeatureFlags,
  isLocalDev,
} from 'lib/localFeatureFlagOverrides';
import { useLocalDispatch } from '../reduxHooks';
import useSampleProject from '../useSampleProjects/useSampleProject';

export { FEATURE_FLAGS };
export type { FeatureFlag };

export const useIsFeatureOn = () => {
  const dispatch = useLocalDispatch();
  const { userHasGlobalPermission } = useGlobalPermissions();
  const { inSampleProject } = useSampleProject();

  const queryClient = useQueryClient();

  const {
    data: features,
    isLoading: isLoadingFeatures,
    isError: didFeaturesError,
  } = useQuery(['features'], api.getFeatures, {
    retry: 3,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !inSampleProject,
    onSuccess: features => {
      queryClient.setQueryData(['features'], features);
      const isUnderMaintenance = isFeatureEnabled(features, 'site_maintenance');
      dispatch(
        setIsSiteUnavailable(
          isUnderMaintenance &&
            !userHasGlobalPermission(GLOBAL_PERMISSION.PLATFORM_ADMIN),
        ),
      );
    },
  });

  const { data: localFeatures } = useQuery(
    ['local-feature-flags'],
    getLocalFeatureFlags,
    {
      enabled: isLocalDev(),
      staleTime: 30000,
      cacheTime: 30000,
      refetchOnWindowFocus: true,
    },
  );

  const mergedFeatures = useMemo((): FeaturesResponse | undefined => {
    if (!features || !isLocalDev() || !localFeatures) {
      return features;
    }

    return features.map(feature => {
      if (feature.name in localFeatures) {
        return { ...feature, enabled: localFeatures[feature.name] };
      }
      return feature;
    });
  }, [features, localFeatures]);

  const isFeatureOn = useCallback(
    (featureName: FeatureFlag) => {
      if (inSampleProject) {
        return true;
      }
      return isFeatureEnabled(mergedFeatures ?? [], featureName);
    },
    [inSampleProject, mergedFeatures],
  );

  return {
    features: mergedFeatures,
    isLoadingFeatures: !inSampleProject && isLoadingFeatures,
    didFeaturesError,
    isFeatureOn,
  };
};
